import React, { useState } from 'react';
import * as consts from 'config/consts';
import { formatNumber, formatDateForString } from 'utils/formatter';
import { useScroll } from 'hooks/useScroll';
import { IncomeGainItemProps } from 'pages/DividendDeposit';
import { ProductTypesLabel } from 'components/features/utils/productTypes';
import { DividendDepositModal } from './DividendTransition';
import { DividendPieChart, DividendPieChartNoData } from './DividendChart';
import { DividendDetailInfo } from '../dividendDetailDeposit/DividendDetailInfo';
import { getSummaryIncomeGain } from 'pages/DividendDetailDeposit';

const DividendDetailItem = ({
  targetIncomeGain,
  arrIncomeGain,
}: {
  targetIncomeGain: IncomeGainItemProps;
  arrIncomeGain: IncomeGainItemProps[];
}) => {
  // 配当詳細スライド
  const [showMenu, setShowMenu] = useState<boolean>(false);
  const [hideAnimate, setHideAnimate] = useState<boolean>(false);

  const menuShow = (): void => {
    setShowMenu(true);
    setHideAnimate(false);
  };

  const menuHide = (): void => {
    setShowMenu(false);
  };

  const menuHideAnimate = (): void => {
    setHideAnimate(true);
  };

  const isDataCooperation = /^MONO/.test(targetIncomeGain.isin);

  const isShowDividendDetailInfoNews = [
    consts.productTypes.国内株式,
    consts.productTypes['国内ETF・ETN'],
    consts.productTypes.国内REIT,
  ].some(productType => productType === targetIncomeGain.productType);

  return (
    <>
      <div className="px-4 py-3 border-b border-gray-300">
        <div
          className="grid items-enter"
          style={{ gridTemplateColumns: '1fr 1fr' }}>
          <div className="ml-2">
            {isDataCooperation ? (
              <div className="flex flex-row items-center text-red">
                <div className="icon-alert text-sm" />
                <div className="ml-1 text-xs">データ取得中</div>
              </div>
            ) : (
              <div className="flex flex-row">
                <ProductTypesLabel
                  productType={targetIncomeGain.productType}
                  cashMargin={consts.CashMargin.現物}
                />
                <div className="ml-1 text-xs text-bluegray-500">
                  {targetIncomeGain.symbol}
                </div>
              </div>
            )}
            <div className="flex flex-col line-clamp-2">
              <div
                className={`mt-2 font-bold ${
                  isDataCooperation ? 'text-bluegray-300' : 'text-bluegray-700'
                }`}>
                {targetIncomeGain.symbolName}
              </div>
            </div>
          </div>
          <div className="flex flex-row items-center">
            <div className="ml-auto" style={{ whiteSpace: 'nowrap' }}>
              <div className="flex flex-col">
                <div
                  className={`${
                    isDataCooperation
                      ? 'text-bluegray-300'
                      : 'text-bluegray-700'
                  }`}>
                  {targetIncomeGain.amount === null
                    ? '---'
                    : formatNumber(targetIncomeGain.amount, {})}
                  <span className="ml-1 text-xs">円</span>
                </div>
              </div>
            </div>
            {isDataCooperation ? (
              <div className="w-10 ml-2" />
            ) : (
              <>
                <div
                  className="flex items-center justify-center w-10 ml-2 cursor-pointer"
                  onClick={!showMenu ? menuShow : menuHideAnimate}>
                  <img src="/static/images/dividendInfo_button.svg" alt="" />
                  {isShowDividendDetailInfoNews && targetIncomeGain.hasNew ? (
                    <span className="rounded-full h-2 w-2 bg-tradewell -mt-5 -ml-2 z-10 "></span>
                  ) : (
                    <></>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      {showMenu ? (
        <DividendDetailInfo
          {...{
            menuHide,
            hideAnimate,
            targetIncomeGain,
            arrIncomeGain,
          }}
        />
      ) : (
        <></>
      )}
    </>
  );
};

export const DividendDetailList = ({
  incomeGainData,
  displayIncomeGainData,
  totalDividend,
}: {
  incomeGainData: IncomeGainItemProps[];
  displayIncomeGainData: IncomeGainItemProps[];
  totalDividend: number;
}) => {
  useScroll();
  return (
    <>
      <div
        className="grid mt-4 px-4 py-1 text-sm text-bluegray-550 font-bold bg-gray-300"
        style={{ gridTemplateColumns: '1fr 1fr' }}>
        <div className="ml-2">銘柄</div>
        <div>入金額</div>
      </div>
      {displayIncomeGainData.map((item, i) => (
        <DividendDetailItem
          {...{
            targetIncomeGain: item,
            arrIncomeGain: incomeGainData,
          }}
          key={i}
        />
      ))}
      <div className="flex flex-row items-center pl-6 pr-4 pt-2 pb-8">
        <div className="font-bold">合計</div>
        <div className="ml-auto">
          <span className="mr-1 text-lg font-bold">
            {formatNumber(totalDividend, {})}
          </span>
          円
        </div>
      </div>
    </>
  );
};

// 内訳
export const DividendClassification = ({
  incomeGainData,
  totalDividend,
  updTime,
}: {
  incomeGainData: IncomeGainItemProps[];
  totalDividend: number;
  updTime: string;
}) => {
  const summaryIncomeGainData = getSummaryIncomeGain(incomeGainData);

  return (
    <>
      <div className="my-2 mx-1">
        {incomeGainData.length === 0 ? (
          <DividendPieChartNoData />
        ) : (
          <DividendPieChart {...{ incomeGainData: summaryIncomeGainData }} />
        )}
      </div>
      <div className="mr-4 mt-4 text-xs text-bluegray-500 text-right">
        {formatDateForString(updTime, 'yyyy/MM/dd(ddd) HH:mm')} 更新
      </div>
      <DividendDepositModal />
      <DividendDetailList
        {...{
          displayIncomeGainData: summaryIncomeGainData,
          incomeGainData,
          totalDividend,
        }}
      />
    </>
  );
};
